import { useMemo } from 'react'

import type { SecondData } from '../types'
import { useUAInsightsMeta } from './useUAInsightsMeta'
import { useUATaxonomy } from './useUATaxonomy'

export interface MotivationRows {
  /** Motivation codes in canonical display order (e.g. `excitement_thrill`). */
  motivationKeysInOrder: string[]
  /** Motivation code → localised label. */
  motivationKeyToLabel: Record<string, string>
}

/**
 * Motivation row codes + labels for the creative timeline, in the canonical
 * `/meta` order. Any code present in the creative's per-second `intensity`
 * maps but missing from `/meta` is appended in first-seen order, so an
 * unexpected motivation still renders instead of disappearing.
 */
export const useMotivationRows = (seconds: SecondData[]): MotivationRows => {
  const { motivations } = useUAInsightsMeta()
  const { motivationLabel } = useUATaxonomy()

  return useMemo<MotivationRows>(() => {
    const codes = motivations.map((m) => m.code)
    const known = new Set(codes)
    seconds.forEach((second) => {
      Object.keys(second.intensity).forEach((code) => {
        if (!known.has(code)) {
          known.add(code)
          codes.push(code)
        }
      })
    })
    return { motivationKeysInOrder: codes, motivationKeyToLabel: Object.fromEntries(codes.map((code) => [code, motivationLabel(code)])) }
  }, [seconds, motivations, motivationLabel])
}
